const NUMBER_REGEX = /^-?\d+(\.\d+)?$/;

const getValueFromRef = (reference, context) => {
  if (reference == null) return undefined;
  const ref = String(reference).trim();
  if (!ref) return undefined;

  const [prefix, ...fieldParts] = ref.split(".");
  const fieldName = fieldParts.join(".").trim();

  if (fieldName) {
    if (prefix === "db") return context?.db?.[fieldName];
    if (prefix === "head") return context?.head?.[fieldName];
    if (prefix === "item") return context?.item?.[fieldName];
  }

  return (
    context?.head?.[ref] ??
    context?.db?.[ref] ??
    context?.item?.[ref] ??
    undefined
  );
};

const resolveOperandValue = (operand, context) => {
  if (operand == null) return undefined;
  const raw = String(operand).trim();
  if (!raw) return undefined;

  if (NUMBER_REGEX.test(raw)) {
    return Number(raw);
  }

  return getValueFromRef(raw, context);
};

const evaluatePlusMinusExpression = (expression, context) => {
  if (expression == null) return undefined;

  const tokens = String(expression)
    .split(/(\+|-)/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) return undefined;

  let result;
  let pendingOperator = "+";

  for (const token of tokens) {
    if (token === "+" || token === "-") {
      pendingOperator = token;
      continue;
    }

    const resolved = resolveOperandValue(token, context);
    const numericValue = Number.parseFloat(resolved);
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0;

    if (result === undefined) {
      result = safeValue;
    } else {
      result = pendingOperator === "+" ? result + safeValue : result - safeValue;
    }
  }

  return result;
};

const isExpression = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /\+|-/g.test(trimmed) && /(db\.|head\.|item\.|\d)/.test(trimmed);
};

const resolveSourceValue = (source, context) => {
  if (source == null) return undefined;

  if (isExpression(source)) {
    return evaluatePlusMinusExpression(source, context);
  }

  const direct = resolveOperandValue(source, context);
  if (direct !== undefined) return direct;

  return source;
};

export const resolveValidationFieldUpdate = (update, context) => {
  if (!update) return null;

  const sourceFieldName = update.copy_from_field_name;
  const destinationFieldName = update.copy_to_field_name;
  const sourceType = update.copy_from;
  const hasSourceFieldName =
    typeof sourceFieldName === "string" && sourceFieldName.trim() !== "";
  const hasExpressionDestination = isExpression(destinationFieldName);
  const hasExpressionSource = isExpression(sourceFieldName);
  const hasDestinationFieldName =
    typeof destinationFieldName === "string" &&
    destinationFieldName.trim() !== "";

  const isNewExpressionMode = hasSourceFieldName && hasExpressionDestination;

  if (isNewExpressionMode) {
    return {
      targetField: sourceFieldName,
      value: resolveSourceValue(destinationFieldName, context),
    };
  }

  const isSourceExpressionMode = hasExpressionSource && hasDestinationFieldName;

  if (isSourceExpressionMode) {
    return {
      targetField: destinationFieldName,
      value: resolveSourceValue(sourceFieldName, context),
    };
  }

  if (sourceType === "db") {
    return {
      targetField: destinationFieldName,
      value: getValueFromRef(`db.${sourceFieldName}`, context),
    };
  }

  if (sourceType === "head") {
    return {
      targetField: destinationFieldName,
      value: getValueFromRef(`head.${sourceFieldName}`, context),
    };
  }

  if (sourceType === "item") {
    return {
      targetField: destinationFieldName,
      value: getValueFromRef(`item.${sourceFieldName}`, context),
    };
  }

  return {
    targetField: destinationFieldName,
    value: resolveSourceValue(sourceFieldName, context),
  };
};
